import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuditPaged, getAuditPeriods, auditExportUrl } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { History, Search, ArrowUpRight, Loader2, FileText, FileSpreadsheet, ExternalLink } from "lucide-react";

const PAGE_SIZE = 25;
const PERIODS = [["all", "All time"], ["weekly", "Week"], ["monthly", "Month"], ["yearly", "Year"]];

const ACTION_META = {
  extracted: { color: "bg-indigo-400", text: "text-indigo-400", label: "Extracted" },
  auto_coded: { color: "bg-sky-400", text: "text-sky-400", label: "Auto-Coded" },
  flagged: { color: "bg-amber-500", text: "text-amber-500", label: "Flagged" },
  recoded: { color: "bg-purple-400", text: "text-purple-400", label: "Recoded" },
  approved: { color: "bg-emerald-500", text: "text-emerald-500", label: "Approved" },
  rejected: { color: "bg-red-500", text: "text-red-500", label: "Rejected" },
  exported: { color: "bg-cyan-400", text: "text-cyan-400", label: "Exported" },
  commented: { color: "bg-pink-400", text: "text-pink-400", label: "Comment" },
  assigned: { color: "bg-violet-400", text: "text-violet-400", label: "Assigned" },
  memory_reset: { color: "bg-orange-400", text: "text-orange-400", label: "Memory Reset" },
  paid: { color: "bg-emerald-400", text: "text-emerald-400", label: "Paid" },
  notified: { color: "bg-rose-400", text: "text-rose-400", label: "Emailed" },
  payment_run: { color: "bg-sky-400", text: "text-sky-400", label: "Payment Run" },
  gmail_connected: { color: "bg-rose-400", text: "text-rose-400", label: "Gmail" },
  gmail_disconnected: { color: "bg-rose-400", text: "text-rose-400", label: "Gmail" },
  gmail_sync_failed: { color: "bg-red-500", text: "text-red-500", label: "Gmail Sync Failed" },
  gmail_sync_recovered: { color: "bg-emerald-500", text: "text-emerald-500", label: "Gmail Sync OK" },
  gmail_autopoll_disabled: { color: "bg-red-500", text: "text-red-500", label: "Gmail Auto-check Off" },
  alert_sent: { color: "bg-amber-500", text: "text-amber-500", label: "Alert Emailed" },
  sender_trusted: { color: "bg-emerald-400", text: "text-emerald-400", label: "Trusted Sender" },
  fx_override: { color: "bg-sky-400", text: "text-sky-400", label: "FX Rate" },
  statement_matched: { color: "bg-teal-400", text: "text-teal-400", label: "Statement" },
  nudged: { color: "bg-red-400", text: "text-red-400", label: "SLA Nudge" },
  escalated: { color: "bg-red-600", text: "text-red-500", label: "Escalated" },
  chase_closed: { color: "bg-teal-400", text: "text-teal-400", label: "Chase" },
  close_pack: { color: "bg-indigo-400", text: "text-indigo-400", label: "Close Pack" },
  report: { color: "bg-indigo-400", text: "text-indigo-400", label: "Report" },
  inbound_address: { color: "bg-rose-400", text: "text-rose-400", label: "Forwarding Address" },
  permission_changed: { color: "bg-indigo-400", text: "text-indigo-400", label: "Permission Changed" },
  policy_changed: { color: "bg-indigo-400", text: "text-indigo-400", label: "Policy Changed" },
  role_changed: { color: "bg-indigo-400", text: "text-indigo-400", label: "Role Changed" },
  corrected: { color: "bg-sky-400", text: "text-sky-400", label: "Corrected" },
  split: { color: "bg-sky-400", text: "text-sky-400", label: "Split / Merge" },
  xero_drift: { color: "bg-amber-500", text: "text-amber-500", label: "Changed in Xero" },
  export_failed: { color: "bg-red-500", text: "text-red-500", label: "Sync Failed" },
  duplicate_resolved: { color: "bg-amber-500", text: "text-amber-500", label: "Duplicate Decision" },
  fx_alert: { color: "bg-sky-400", text: "text-sky-400", label: "FX Alert" },
  tracking: { color: "bg-sky-400", text: "text-sky-400", label: "Tracking" },
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [period, setPeriod] = useState("all");
  const [anchor, setAnchor] = useState("");
  const [periods, setPeriods] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { getAuditPeriods().then(setPeriods).catch(() => setPeriods({ weekly: [], monthly: [], yearly: [] })); }, []);
  const options = period === "all" ? [] : periods?.[period] || [];
  const effectiveAnchor = options.some((o) => o.anchor === anchor) ? anchor : options[0]?.anchor || "";
  const periodLabel = period === "all" ? "All time" : options.find((o) => o.anchor === effectiveAnchor)?.label || "";
  const exportParams = { period, anchor: effectiveAnchor, q: term || undefined };

  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchPage = useCallback(
    async (offset) => {
      if (period !== "all" && !effectiveAnchor) return;
      const res = await getAuditPaged({ q: term || undefined, period, anchor: period === "all" ? undefined : effectiveAnchor, limit: PAGE_SIZE, offset });
      setTotal(res.total);
      setEntries((prev) => (offset === 0 ? res.items : [...prev, ...res.items]));
    },
    [term, period, effectiveAnchor]
  );

  useEffect(() => {
    setLoading(true);
    fetchPage(0).finally(() => setLoading(false));
  }, [fetchPage]);

  const loadMore = async () => {
    setMore(true);
    await fetchPage(entries.length);
    setMore(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Audit Trail</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Immutable record of every extraction, code change, approval, rejection, export and note.
        </p>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="audit-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by user, supplier, invoice or action…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-border bg-card p-0.5" data-testid="audit-period-tabs">
            {PERIODS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPeriod(k)} data-testid={`audit-period-${k}`}
                className={cn("rounded-md px-3 py-1.5 text-xs font-medium transition-colors", period === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
                {l}
              </button>
            ))}
          </div>
          {period !== "all" && (
            <select value={effectiveAnchor} onChange={(e) => setAnchor(e.target.value)} className="h-9 max-w-[280px] rounded-md border bg-background px-2 text-sm" data-testid="audit-period-anchor">
              {options.map((o) => <option key={o.anchor} value={o.anchor}>{o.label}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2 lg:ml-auto">
          <a href={auditExportUrl({ ...exportParams, format: "pdf" })} target="_blank" rel="noreferrer" data-testid="audit-export-pdf"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-muted">
            <FileText className="h-3.5 w-3.5 text-red-500" /> PDF <ExternalLink className="h-3 w-3 text-muted-foreground" />
          </a>
          <a href={auditExportUrl({ ...exportParams, format: "xlsx" })} data-testid="audit-export-xlsx"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-muted">
            <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" /> Excel
          </a>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="border-b border-border px-5 py-3 text-sm font-medium flex items-center gap-2" data-testid="audit-count">
          <History className="h-4 w-4" /> {loading ? "Loading…" : `${entries.length} of ${total} events`}
          <span className="text-muted-foreground font-normal" data-testid="audit-period-label">· {periodLabel}</span>
        </div>
        <div className="divide-y divide-border" data-testid="audit-trail-table">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            entries.map((e) => {
              const am = ACTION_META[e.action] || { color: "bg-muted-foreground", text: "text-muted-foreground", label: e.action };
              return (
                <div key={e.id} data-testid="audit-trail-table-row" className="flex items-start gap-4 px-5 py-4 hover:bg-muted/40 transition-colors">
                  <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", am.color)} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">{e.detail}</div>
                    <div className="text-xs text-muted-foreground font-mono mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="font-medium text-foreground/80">{e.user}</span>
                      <span>·</span>
                      <span>{new Date(e.timestamp).toLocaleString()}</span>
                      {(() => {
                        const target = e.has_invoice
                          ? `/invoice/${e.invoice_id}`
                          : { report: "/reports", close_pack: "/reports", statement_matched: "/suppliers", chase_closed: "/suppliers", payment_run: "/payments" }[e.action];
                        if (!target || !e.supplier) return null;
                        return (
                          <>
                            <span>·</span>
                            <button data-testid="audit-link" onClick={() => navigate(target)} className="text-primary hover:underline flex items-center gap-0.5">
                              {e.has_invoice ? `${e.supplier} ${e.invoice_number}` : { report: "Open reports", close_pack: "Open reports", statement_matched: "Open suppliers", chase_closed: "Open suppliers", payment_run: "Open payment runs" }[e.action]}
                              <ArrowUpRight className="h-3 w-3" />
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <span className={cn("text-xs font-semibold whitespace-nowrap", am.text)}>{am.label}</span>
                </div>
              );
            })
          )}
          {!loading && entries.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">No matching events.</div>
          )}
        </div>
        {!loading && entries.length < total && (
          <div className="border-t border-border p-3 flex justify-center">
            <Button data-testid="audit-load-more-btn" variant="outline" size="sm" onClick={loadMore} disabled={more}>
              {more && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Load {Math.min(PAGE_SIZE, total - entries.length)} more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
